import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { CheckCircle2, Clock, LogIn, LogOut } from "lucide-react";

export default function Fichar() {
  const [workers, setWorkers] = useState([]);
  const [workerId, setWorkerId] = useState("");
  const [pin, setPin] = useState("");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testMode, setTestMode] = useState(false);

  const selectedWorker = useMemo(() => workers.find((worker) => worker.id === workerId), [workers, workerId]);

  useEffect(() => {
    Promise.all([
      api.get("/workers/active"),
      api.get("/time-clock/mode"),
    ])
      .then(([workersResponse, modeResponse]) => {
        setWorkers(Array.isArray(workersResponse.data) ? workersResponse.data : []);
        setTestMode(Boolean(modeResponse.data?.test_mode));
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const id = window.setTimeout(() => {
      setWorkerId("");
      setPin("");
      setState(null);
      setSuccess("");
      setError("");
    }, 3500);
    return () => window.clearTimeout(id);
  }, [success]);

  const checkStatus = async () => {
    setBusy("status");
    setError("");
    setSuccess("");
    try {
      const { data } = await api.post("/time-clock/status", { worker_id: workerId, pin });
      setState(data);
      setTestMode(Boolean(data.test_mode));
    } catch (e) {
      setState(null);
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const clock = async () => {
    setBusy("clock");
    setError("");
    try {
      const { data } = await api.post("/time-clock/clock", {
        worker_id: workerId,
        pin,
        type: state?.next_action,
      });
      setSuccess(data.message || "Fichaje registrado");
      setState(null);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const canContinue = workerId && /^\d{4}$/.test(pin);
  const isClockOut = state?.next_action === "clock_out";
  const ActionIcon = isClockOut ? LogOut : LogIn;

  return (
    <main className="min-h-screen bg-accent/40 px-4 py-6 text-foreground">
      <div className="mx-auto max-w-md space-y-5">
        <header className="text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground">
            <Clock size={28} />
          </div>
          <h1 className="mt-4 font-display text-3xl font-black">Fichaje</h1>
          <p className="text-sm text-muted-foreground">Casa del Pueblo · Feria de Albacete</p>
        </header>

        {testMode && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center font-bold text-amber-900">
            <div className="font-display text-xl font-black">MODO PRUEBAS</div>
            <div className="text-sm">Los fichajes realizados ahora son de prueba.</div>
          </div>
        )}

        <section className="space-y-4 rounded-2xl border border-border bg-white p-5 shadow-sm">
          {success ? (
            <div className="rounded-xl bg-emerald-50 p-5 text-center text-emerald-800">
              <CheckCircle2 className="mx-auto mb-3" size={36} />
              <div className="font-display text-xl font-black">{success}</div>
              <div className="mt-2 text-sm font-medium">Volviendo al inicio...</div>
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block font-bold">Trabajador</span>
                <select
                  value={workerId}
                  onChange={(e) => {
                    setWorkerId(e.target.value);
                    setState(null);
                    setError("");
                  }}
                  className="w-full rounded-xl border border-border px-3 py-3 text-base"
                >
                  <option value="">Selecciona tu nombre</option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name} {worker.surname}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                <span className="mb-1 block font-bold">PIN</span>
                <input
                  value={pin}
                  onChange={(e) => {
                    setPin(e.target.value.replace(/\D/g, "").slice(0, 4));
                    setState(null);
                    setError("");
                  }}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full rounded-xl border border-border px-3 py-3 text-center text-2xl font-black tracking-[0.4em]"
                  placeholder="0000"
                />
              </label>

              {error && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm font-bold text-destructive">
                  {error}
                </div>
              )}

              {!state ? (
                <button
                  type="button"
                  onClick={checkStatus}
                  disabled={!canContinue || busy === "status"}
                  className="w-full rounded-full bg-primary px-4 py-3 font-black text-primary-foreground disabled:opacity-60"
                >
                  {busy === "status" ? "Comprobando..." : "Continuar"}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-xl bg-accent/50 p-3 text-sm">
                    <div className="font-bold">{selectedWorker?.name} {selectedWorker?.surname}</div>
                    <div className="text-muted-foreground">
                      {state.status === "inside" ? `Dentro desde ${state.inside_since_local}` : "Ahora mismo estas fuera"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clock}
                    disabled={busy === "clock"}
                    className={`flex w-full items-center justify-center gap-2 rounded-full px-4 py-4 text-lg font-black text-white disabled:opacity-60 ${
                      isClockOut ? "bg-foreground" : "bg-primary"
                    }`}
                  >
                    <ActionIcon size={22} />
                    {busy === "clock" ? "Registrando..." : isClockOut ? "SALIR" : "ENTRAR"}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}
